import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  clearProfilerLogs,
  fetchProfilerStatus,
  fetchProfilerLogs,
  switchProfiler,
} from "@menu/SYS/SN_SYS_BAS_REP_002/profilerApi";
import {
  BRIDGE_MSG_TYPE,
  BridgeReady,
  BridgeRequest,
  BridgeResponse,
} from "@menu/SYS/SN_SYS_BAS_REP_002/profilerBridge";

/**
 * 프로파일러 연결창 (요청 대행).
 *
 * 다른 사이트의 프로파일러 화면이 이 라우트를 팝업으로 열고 postMessage 로 요청을 보낸다.
 * 이 창은 자기 origin 에서 /snotes/api/querySwitch 를 호출해 결과를 opener 에게 돌려준다.
 * 기존 /querySwitch, /getQueryLogs 라우트와 같은 원리이지만, 폴링을 위해 열린 채로 유지된다.
 *
 * 보안: ?origin= 으로 넘어온 opener origin 과 window.opener 에서 온 메시지만 처리한다.
 */
const ProfilerBridge: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [count, setCount] = useState(0);
  const [lastAt, setLastAt] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [warn, setWarn] = useState("");

  useEffect(() => {
    const allowedOrigin = searchParams.get("origin") || "";
    const opener = window.opener as Window | null;

    if (!opener || !allowedOrigin) {
      setWarn("프로파일러 화면에서 열어야 동작하는 창입니다.");
      return;
    }

    const reply = (res: BridgeResponse) =>
      opener.postMessage(res, allowedOrigin);

    const handler = async (e: MessageEvent) => {
      if (e.origin !== allowedOrigin || e.source !== opener) return;
      const msg = e.data as BridgeRequest;
      if (!msg || msg.type !== BRIDGE_MSG_TYPE) return;

      if (msg.action === "close") {
        window.close();
        return;
      }

      try {
        let data: any = null;
        switch (msg.action) {
          case "switch":
            data = await switchProfiler("", msg.tenantId, msg.status === "on");
            break;
          case "getLogs":
            data = await fetchProfilerLogs("", msg.tenantId, false);
            break;
          case "getIpLogs":
            data = await fetchProfilerLogs("", msg.tenantId, true);
            break;
          case "clearLog":
            await clearProfilerLogs("", msg.tenantId);
            break;
          case "status":
            data = await fetchProfilerStatus("", msg.tenantId);
            break;
        }
        reply({ type: BRIDGE_MSG_TYPE, id: msg.id, ok: true, data });
      } catch (err: any) {
        reply({
          type: BRIDGE_MSG_TYPE,
          id: msg.id,
          ok: false,
          error: err?.message || "요청 처리 실패",
        });
      }
      setCount((c) => c + 1);
      setLastAt(new Date().toLocaleTimeString());
      setLastAction(msg.action);
    };

    window.addEventListener("message", handler);
    const ready: BridgeReady = { type: BRIDGE_MSG_TYPE, ready: true };
    opener.postMessage(ready, allowedOrigin);

    return () => window.removeEventListener("message", handler);
  }, [searchParams]);

  return (
    <div className="p-4 text-sm leading-6" style={{ fontFamily: "sans-serif" }}>
      <div className="font-bold">SNOTES 쿼리 프로파일러 연결창</div>
      {warn ? (
        <div className="text-red-600">{warn}</div>
      ) : (
        <>
          <div className="text-gray-600">
            이 창은 프로파일러 화면의 요청을 대신 처리합니다.
            <br />
            추적종료 시
            자동으로 닫히며, 직접 닫으면 추적이 중단됩니다.
          </div>
          <div className="text-gray-500 mt-1">
            처리 {count}건{lastAt && ` / 마지막 ${lastAt} (${lastAction})`}
          </div>
        </>
      )}
    </div>
  );
};

export default ProfilerBridge;
